import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, CheckCircle, ChevronRight, Package, RefreshCw, Search, Truck } from 'lucide-react';
import { DriverBottomNav } from '../components/DriverBottomNav';
import { DriverCoachmark } from '../components/DriverCoachmark';
import { useDriverOnboarding } from '../hooks/useDriverOnboarding';
import { ASSIGNMENTS_PAGE_STEPS_INTERNAL, ASSIGNMENTS_PAGE_STEPS_EXTERNAL } from '../utils/driverOnboardingSteps';
import { PendingAssignmentDetailSheet } from '../components/PendingAssignmentDetailSheet';
import { driverService } from '../services/driverService';
import { shipmentServiceReal } from '../services/shipmentServiceReal';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { getStoredDriverMobileUser } from '../utils/driverMobileSession';
import { toast } from 'sonner';
import { isAnyDriverUser } from '../utils/userRoles';

type PendingAssignment = {
  id: string;
  shipmentNo: string;
  pickupName: string;
  pickupCity: string;
  pickupDistrict?: string;
  deliveryName: string;
  deliveryCity: string;
  deliveryDistrict?: string;
  assignedAt?: string;
  pickupDate?: string;
};

type ActiveShipment = {
  id: string;
  shipmentNo: string;
  pickupName: string;
  pickupCity: string;
  pickupDistrict?: string;
  deliveryName: string;
  deliveryCity: string;
  deliveryDistrict?: string;
  status: string;
  vehiclePlate?: string;
  trailerPlate?: string;
  assignedAt?: string;
  sortDate?: string;
};

const mapProcessStatusLabel = (value?: string, options?: { assignmentAccepted?: boolean; internalDriver?: boolean }) => {
  switch (String(value || '').trim()) {
    case 'atandi-islem-bekliyor':
      if (options?.internalDriver && options?.assignmentAccepted) return 'Yükleme Tamamlandı';
      return 'Atandı İşlem Bekliyor';
    case 'yukleme-bekliyor':
      return 'Yükleme Bekliyor';
    case 'yukleme-devam':
      return 'Yükleme Devam';
    case 'yukleme-tamamlandi':
      return 'Yükleme Tamamlandı';
    case 'tasima-yolunda':
      return 'Teslim Yolunda';
    case 'bosaltma-bekliyor':
      return 'Boşaltma Bekliyor';
    case 'bosaltma-devam':
      return 'Boşaltma Başladı';
    case 'bosaltma-tamamlandi':
      return 'Boşaltma Tamamlandı';
    default:
      return value || '-';
  }
};

export function DriverMobileAssignments() {
  const navigate = useNavigate();
  const { showTour, initialized, advanceTour, skipTour } = useDriverOnboarding('assignments');
  const [user, setUser] = useState<any>(null);
  const [driverProfile, setDriverProfile] = useState<any>(null);
  const [pendingAssignments, setPendingAssignments] = useState<PendingAssignment[]>([]);
  const [activeShipments, setActiveShipments] = useState<ActiveShipment[]>([]);
  const [completedShipments, setCompletedShipments] = useState<ActiveShipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPendingAssignmentId, setSelectedPendingAssignmentId] = useState<string | null>(null);
  const [selectedActiveShipmentId, setSelectedActiveShipmentId] = useState<string | null>(null);
  const [selectedCompletedShipmentId, setSelectedCompletedShipmentId] = useState<string | null>(null);
  const [completedShipmentSearch, setCompletedShipmentSearch] = useState('');
  const isInternalDriver = user?.role === 'internal-driver'
    || user?.driverType === 'internal'
    || driverProfile?.driver_type === 'internal';

  const loadData = async (userId: string) => {
    try {
      const driverResponse: any = await driverService.getDriverByUserId(userId);
      const driver = driverResponse?.data || driverResponse || {};
      setDriverProfile(driver);
      if (!driver?.id) {
        setPendingAssignments([]);
        setActiveShipments([]);
        return;
      }

      const isExternalDriver = driver.driver_type === 'external';

      const [pendingResponse, shipmentsResponse] = await Promise.all([
        shipmentServiceReal.getPendingAssignments(driver.id),
        shipmentServiceReal.getShipments(
          isExternalDriver
            ? { driverId: String(driver.id) }
            : { driverId: String(driver.id), assignmentResponseStatus: 'accepted' }
        ),
      ]);

      const pendingRows = Array.isArray((pendingResponse as any)?.data) ? (pendingResponse as any).data : [];
      setPendingAssignments(pendingRows.map((item: any) => ({
        id: String(item.id),
        shipmentNo: item.shipment_no || String(item.id),
        pickupName: item.pickup_name || 'Yükleme',
        pickupCity: item.pickup_city || '',
        pickupDistrict: item.pickup_district || '',
        deliveryName: item.delivery_name || 'Teslimat',
        deliveryCity: item.delivery_city || '',
        deliveryDistrict: item.delivery_district || '',
        assignedAt: item.assigned_at || item.updated_at || item.created_at || '',
        pickupDate: item.pickup_date || '',
      })));

      const shipmentRows = Array.isArray((shipmentsResponse as any)?.data) ? (shipmentsResponse as any).data : [];
      const activeRows = shipmentRows.filter((item: any) => {
        const processStatus = String(item.process_status || '').trim();
        const shipmentStatus = String(item.status || '').trim().toLowerCase();
        return processStatus !== 'bosaltma-tamamlandi' && shipmentStatus !== 'delivered';
      }).sort((a: any, b: any) => new Date(b.assigned_at || b.updated_at || b.created_at || 0).getTime() - new Date(a.assigned_at || a.updated_at || a.created_at || 0).getTime());
      const completedRows = shipmentRows.filter((item: any) => {
        const processStatus = String(item.process_status || '').trim();
        const shipmentStatus = String(item.status || '').trim().toLowerCase();
        return processStatus === 'bosaltma-tamamlandi' || shipmentStatus === 'delivered';
      }).sort((a: any, b: any) => new Date(b.assigned_at || b.updated_at || b.created_at || 0).getTime() - new Date(a.assigned_at || a.updated_at || a.created_at || 0).getTime());
      setActiveShipments(activeRows.map((item: any) => ({
        id: String(item.id),
        shipmentNo: item.shipment_no || String(item.id),
        pickupName: item.pickup_name || 'Yükleme',
        pickupCity: item.pickup_city || '',
        pickupDistrict: item.pickup_district || '',
        deliveryName: item.delivery_name || 'Teslimat',
        deliveryCity: item.delivery_city || '',
        deliveryDistrict: item.delivery_district || '',
        status: mapProcessStatusLabel(item.process_status || item.status || '-', { internalDriver: true, assignmentAccepted: String(item.assignment_response_status || '').trim() === 'accepted' }),
        vehiclePlate: item.assigned_vehicle_plate || item.cekici_plaka || driver.assigned_vehicle_plate || '-',
        trailerPlate: item.assigned_trailer_plate || item.dorse_plaka || driver.assigned_trailer_plate || '-',
        assignedAt: item.assigned_at || '',
        sortDate: item.assigned_at || item.updated_at || item.created_at || '',
      })));
      setCompletedShipments(completedRows.map((item: any) => ({
        id: String(item.id),
        shipmentNo: item.shipment_no || String(item.id),
        pickupName: item.pickup_name || 'Yükleme',
        pickupCity: item.pickup_city || '',
        pickupDistrict: item.pickup_district || '',
        deliveryName: item.delivery_name || 'Teslimat',
        deliveryCity: item.delivery_city || '',
        deliveryDistrict: item.delivery_district || '',
        status: mapProcessStatusLabel(item.process_status || item.status || '-', { internalDriver: true, assignmentAccepted: String(item.assignment_response_status || '').trim() === 'accepted' }),
        vehiclePlate: item.assigned_vehicle_plate || item.cekici_plaka || driver.assigned_vehicle_plate || '-',
        trailerPlate: item.assigned_trailer_plate || item.dorse_plaka || driver.assigned_trailer_plate || '-',
        assignedAt: item.assigned_at || '',
        sortDate: item.assigned_at || item.updated_at || item.created_at || '',
      })));
    } catch (error) {
      console.error('Driver assignments load failed:', error);
      toast.error('Sevkiyatlar yüklenemedi');
    }
  };

  useEffect(() => {
    const storedUser = getStoredDriverMobileUser<any>();
    if (!storedUser) {
      navigate('/driver-mobile-auth', { replace: true });
      return;
    }
    if (!isAnyDriverUser(storedUser)) {
      navigate('/driver-mobile-dashboard', { replace: true });
      return;
    }

    setUser(storedUser);
    loadData(String(storedUser.id)).finally(() => setLoading(false));
  }, [navigate]);

  useAutoRefresh(
    () => {
      if (!user?.id) return;
      return loadData(String(user.id));
    },
    { intervalMs: 15000, enabled: Boolean(user?.id) }
  );

  const handleRefresh = async () => {
    if (!user) return;
    setRefreshing(true);
    await loadData(String(user.id));
    setRefreshing(false);
  };

  const handleRespondAssignment = async (assignmentId: string, action: 'accepted' | 'rejected') => {
    if (action === 'accepted' && !(driverProfile?.assigned_trailer_plate || driverProfile?.dorse_plaka)) {
      toast.error('Görevi kabul etmeden önce aktif dorse seçmelisiniz');
      return;
    }
    try {
      const rejectNote = action === 'rejected'
        ? window.prompt('Red nedeni giriniz')?.trim()
        : undefined;
      if (action === 'rejected' && !rejectNote) {
        toast.error('Red nedeni zorunludur');
        return;
      }

      await shipmentServiceReal.respondAssignment(assignmentId, { action, note: rejectNote });
      toast.success(action === 'accepted' ? 'Görev onaylandı' : 'Görev reddedildi');
      if (user) await loadData(String(user.id));
    } catch (error) {
      console.error('Assignment response failed:', error);
      toast.error((error as any)?.message || 'Görev yanıtı kaydedilemedi');
    }
  };

  const filteredCompletedShipments = useMemo(() => {
    const query = completedShipmentSearch.trim().toLocaleLowerCase('tr-TR');
    if (!query) return completedShipments;
    return completedShipments.filter((shipment) => {
      const haystack = [shipment.shipmentNo, shipment.pickupName, shipment.pickupCity, shipment.deliveryName, shipment.deliveryCity, shipment.vehiclePlate, shipment.trailerPlate].join(' ').toLocaleLowerCase('tr-TR');
      return haystack.includes(query);
    });
  }, [completedShipments, completedShipmentSearch]);

  if (!user) return null;

  return (
    <div className="safe-area-page min-h-dvh bg-gray-50 dark:bg-[#0b0b0b]">
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-white p-4 pb-6 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-blue-900 dark:to-blue-800 dark:text-white dark:shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/driver-mobile-home')} className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-slate-900 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Sevkiyatlarım</h1>
              <p className="text-sm text-slate-500 dark:text-blue-200">Atanan görevleri onaylayın ve süreçleri takip edin</p>
            </div>
          </div>
          <button onClick={handleRefresh} disabled={refreshing} className="rounded-lg p-2 text-slate-700 transition-colors hover:bg-slate-100 dark:text-white dark:hover:bg-blue-800">
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="px-4 py-4 space-y-6">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
            <p className="text-xs text-slate-500">Çekici</p>
            <p className="mt-2 text-lg font-bold text-slate-900">{driverProfile?.assigned_vehicle_plate || driverProfile?.cekici_plaka || '-'}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
            <p className="text-xs text-slate-500">Dorse</p>
            <p className="mt-2 text-lg font-bold text-slate-900">{driverProfile?.assigned_trailer_plate || driverProfile?.dorse_plaka || '-'}</p>
          </div>
        </div>

        <div data-tour="pending-tasks" className="space-y-3">
          <h2 className="text-lg font-bold text-gray-900">Kabul Bekleyen Görevler</h2>
          {loading ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-sm text-slate-500">Yükleniyor...</div>
          ) : pendingAssignments.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-sm text-slate-500">Kabul bekleyen görev yok</div>
          ) : pendingAssignments.map((assignment) => (
            <div key={assignment.id} className="bg-white rounded-2xl shadow-sm border border-amber-200 p-4">
              <button type="button" onClick={() => setSelectedPendingAssignmentId(assignment.id)} className="mb-4 w-full text-left">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="text-sm text-amber-700 font-semibold">#{assignment.shipmentNo}</div>
                  <div className="text-xs text-gray-500 mt-1">Atama: {assignment.assignedAt ? new Date(assignment.assignedAt).toLocaleString('tr-TR') : '-'}</div>
                </div>
                <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">Onay Bekliyor</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3 min-w-0">
                  <p className="text-xs font-medium text-emerald-700 mb-2">Yükleme</p>
                  <p className="text-sm font-bold text-gray-900 truncate">{assignment.pickupName}</p>
                  <p className="text-xs text-gray-500 mt-1 truncate">{[assignment.pickupCity, assignment.pickupDistrict].filter(Boolean).join(' / ')}</p>
                </div>
                <div className="rounded-2xl bg-rose-50 border border-rose-100 p-3 min-w-0">
                  <p className="text-xs font-medium text-rose-700 mb-2">Boşaltma</p>
                  <p className="text-sm font-bold text-gray-900 truncate">{assignment.deliveryName}</p>
                  <p className="text-xs text-gray-500 mt-1 truncate">{[assignment.deliveryCity, assignment.deliveryDistrict].filter(Boolean).join(' / ')}</p>
                </div>
              </div>
              <div className="inline-flex items-center gap-2 text-sm font-medium text-amber-700">
                Detayı Gör <ChevronRight className="h-4 w-4" />
              </div>
              </button>
              <div className={`grid gap-2 ${isInternalDriver ? 'grid-cols-1' : 'grid-cols-2'}`}>
                 <button onClick={() => handleRespondAssignment(assignment.id, 'accepted')} className="rounded-xl bg-green-600 text-white py-3 font-bold hover:bg-green-700">Onayla</button>
                 {!isInternalDriver && <button onClick={() => handleRespondAssignment(assignment.id, 'rejected')} className="rounded-xl bg-red-600 text-white py-3 font-bold hover:bg-red-700">Reddet</button>}
               </div>
             </div>
           ))}
        </div>

        <div data-tour="active-assignments" className="space-y-3">
          <h2 className="text-lg font-bold text-gray-900">Atanan Sevkiyatlar</h2>
          {loading ? null : activeShipments.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-sm text-slate-500">Atanmış aktif sevkiyat yok</div>
          ) : activeShipments.map((shipment) => (
            <div key={shipment.id} className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-4 text-left">
              <button type="button" onClick={() => setSelectedActiveShipmentId(shipment.id)} className="w-full text-left">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-2xl bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                    <Truck className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">#{shipment.shipmentNo}</div>
                    <div className="text-xs text-slate-500 mt-1">{shipment.vehiclePlate || '-'} / {shipment.trailerPlate || '-'}</div>
                    <div className="text-xs text-slate-500 mt-1">Atama: {shipment.assignedAt ? new Date(shipment.assignedAt).toLocaleString('tr-TR') : '-'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">{shipment.status}</span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3 min-w-0">
                  <p className="text-xs font-medium text-emerald-700 mb-2">Yükleme</p>
                  <p className="text-sm font-bold text-gray-900 truncate">{shipment.pickupName}</p>
                  <p className="text-xs text-gray-500 mt-1 truncate">{[shipment.pickupCity, shipment.pickupDistrict].filter(Boolean).join(' / ')}</p>
                </div>
                <div className="rounded-2xl bg-rose-50 border border-rose-100 p-3 min-w-0">
                  <p className="text-xs font-medium text-rose-700 mb-2">Boşaltma</p>
                  <p className="text-sm font-bold text-gray-900 truncate">{shipment.deliveryName}</p>
                  <p className="text-xs text-gray-500 mt-1 truncate">{[shipment.deliveryCity, shipment.deliveryDistrict].filter(Boolean).join(' / ')}</p>
                </div>
              </div>
              <div className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-slate-600">
                Detayı Gör <ChevronRight className="w-4 h-4" />
              </div>
              </button>
              <button onClick={() => navigate(`/driver-mobile-shipment-tracking/${shipment.id}`)} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white hover:bg-blue-700">
                <Package className="w-4 h-4" /> Süreci Aç ve İlerle
              </button>
            </div>
          ))}
        </div>

        <div data-tour="past-shipments" className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-gray-900">Geçmiş Sevkiyatlar</h2>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{filteredCompletedShipments.length} kayıt</span>
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={completedShipmentSearch}
              onChange={(e) => setCompletedShipmentSearch(e.target.value)}
              placeholder="Geçmiş sevkiyat ara"
              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none"
            />
          </div>
          <div className="max-h-[28rem] space-y-3 overflow-y-auto pr-1">
            {loading ? null : filteredCompletedShipments.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-sm text-slate-500">Geçmiş sevkiyat bulunamadı</div>
            ) : filteredCompletedShipments.map((shipment) => (
              <div key={shipment.id} className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-4 text-left">
                <button type="button" onClick={() => setSelectedCompletedShipmentId(shipment.id)} className="w-full text-left">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-11 h-11 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">#{shipment.shipmentNo}</div>
                        <div className="text-xs text-slate-500 mt-1">{shipment.vehiclePlate || '-'} / {shipment.trailerPlate || '-'}</div>
                        <div className="text-xs text-slate-500 mt-1">Atama: {shipment.assignedAt ? new Date(shipment.assignedAt).toLocaleString('tr-TR') : '-'}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">{shipment.status}</span>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3 min-w-0">
                      <p className="text-xs font-medium text-emerald-700 mb-2">Yükleme</p>
                      <p className="text-sm font-bold text-gray-900 truncate">{shipment.pickupName}</p>
                      <p className="text-xs text-gray-500 mt-1 truncate">{[shipment.pickupCity, shipment.pickupDistrict].filter(Boolean).join(' / ')}</p>
                    </div>
                    <div className="rounded-2xl bg-rose-50 border border-rose-100 p-3 min-w-0">
                      <p className="text-xs font-medium text-rose-700 mb-2">Boşaltma</p>
                      <p className="text-sm font-bold text-gray-900 truncate">{shipment.deliveryName}</p>
                      <p className="text-xs text-gray-500 mt-1 truncate">{[shipment.deliveryCity, shipment.deliveryDistrict].filter(Boolean).join(' / ')}</p>
                    </div>
                  </div>
                  <div className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-slate-600">
                    Detayı Gör <ChevronRight className="w-4 h-4" />
                  </div>
                </button>
                <button onClick={() => navigate(`/driver-mobile-shipment-tracking/${shipment.id}`)} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white hover:bg-emerald-700">
                  <Package className="w-4 h-4" /> Sevkiyati Ac
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {initialized && showTour && (
        <DriverCoachmark
          steps={isInternalDriver ? ASSIGNMENTS_PAGE_STEPS_INTERNAL : ASSIGNMENTS_PAGE_STEPS_EXTERNAL}
          onFinish={() => {
            const firstActive = activeShipments[0];
            if (firstActive?.id) {
              advanceTour('tracking');
              navigate(`/driver-mobile-shipment-tracking/${firstActive.id}?tour=1`);
            } else {
              advanceTour(null);
              navigate('/driver-mobile-home');
            }
          }}
          onSkip={skipTour}
        />
      )}

      <DriverBottomNav />
      <PendingAssignmentDetailSheet shipmentId={selectedPendingAssignmentId} open={Boolean(selectedPendingAssignmentId)} onClose={() => setSelectedPendingAssignmentId(null)} title="Kabul Bekleyen Görev" />
      <PendingAssignmentDetailSheet shipmentId={selectedActiveShipmentId} open={Boolean(selectedActiveShipmentId)} onClose={() => setSelectedActiveShipmentId(null)} title="Atanan Sevkiyat" />
      <PendingAssignmentDetailSheet shipmentId={selectedCompletedShipmentId} open={Boolean(selectedCompletedShipmentId)} onClose={() => setSelectedCompletedShipmentId(null)} title="Geçmiş Sevkiyat" />
    </div>
  );
}
